<script setup lang="ts">
  import { Typo } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import { useSearchCriteria, type SearchCriteria } from '@/entities/searchCriteria';
  import type { AccountMenuKey, AccountUser } from '@/features/account';
  import { type CountryResortsUrl, type FormDataUrl, type IntelligentSearchUrl } from '@/shared/config/formData';
  import { LocalStorageKey } from '@/shared/config/localStorage';
  import { ProductType } from '@/shared/config/pages';
  import IconTab from '@/shared/ui/IconTab.vue';
  import { HOTEL_SEARCH_FORM_TEXTS, SearchForm } from '@/widgets/searchForm';
  import { SiteHeader } from '@/widgets/siteHeader';
  import { SHOWCASE_INDEX_TITLE } from '../config/showcase';
  import tabs from '../config/tabs';
  import { type TabType } from '../model/types';

  // Повторяет ShowcaseIndexProps: compiler-sfc Vue 2.7 не резолвит импортированный тип в defineProps.
  interface ShowcaseIndexPageProps {
    user?: AccountUser | null;
    accountUrl?: string;
    accountMenuKeys?: AccountMenuKey[];
    title?: string;
    formUrl?: string;
    searchUrl?: string;
    resortsUrl?: string;
    isVisibleRoomButton?: boolean;
    hotelsFormUrl?: string;
    hotelsSearchUrl?: string;
    hotelsResortsUrl?: string;
  }

  const props = withDefaults(defineProps<ShowcaseIndexPageProps>(), {
    user: null,
    accountUrl: '',
    accountMenuKeys: undefined,
    title: SHOWCASE_INDEX_TITLE,
    formUrl: '',
    searchUrl: '',
    resortsUrl: '',
    isVisibleRoomButton: true,
    hotelsFormUrl: '',
    hotelsSearchUrl: '',
    hotelsResortsUrl: '',
  });

  interface ShowcaseIndexPageEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
    (e: 'search', criteria: SearchCriteria, productType: ProductType): void;
  }
  const emit = defineEmits<ShowcaseIndexPageEmits>();

  // Как на главной: таб переключает форму, выдача открывается только по кнопке поиска.
  const toursTab = tabs.find(({ type }) => type === 'tours')!;
  const hotelsTab = tabs.find(({ type }) => type === 'hotels')!;

  const activeTab = ref<TabType>('tours');
  const isToursOpen = computed(() => activeTab.value === 'tours');
  const isHotelsOpen = computed(() => activeTab.value === 'hotels');

  const toursModel = useSearchCriteria({
    lastCriteriaKey: LocalStorageKey.TourCriteria,
    isOpen: isToursOpen,
    formUrl: props.formUrl as FormDataUrl,
    searchUrl: props.searchUrl as IntelligentSearchUrl,
    resortsUrl: props.resortsUrl as CountryResortsUrl,
  });
  const hotelsModel = useSearchCriteria({
    lastCriteriaKey: LocalStorageKey.TourCriteriaHotelOffers,
    isOpen: isHotelsOpen,
    formUrl: props.hotelsFormUrl as FormDataUrl,
    searchUrl: props.hotelsSearchUrl as IntelligentSearchUrl,
    resortsUrl: props.hotelsResortsUrl as CountryResortsUrl,
  });

  // Переход делает хост: у витрины свой роутинг и метки поиска.
  const onToursSearch = (criteria: SearchCriteria): void => {
    toursModel.saveSearch(criteria);
    emit('search', criteria, ProductType.TourOffer);
  };
  const onHotelsSearch = (criteria: SearchCriteria): void => {
    hotelsModel.saveSearch(criteria);
    emit('search', criteria, ProductType.HotelOffer);
  };
</script>

<template>
  <div
    class="showcase-index"
    data-testid="showcase-index"
  >
    <SiteHeader
      :user="user"
      :account-url="accountUrl"
      :account-menu-keys="accountMenuKeys"
      :visible-logos="false"
      :visible-skolkovo="false"
      :should-adaptive="false"
      :primary="[]"
      :more="[]"
      account-button-size="l"
      @login="emit('login')"
      @logout="emit('logout')"
    >
      <template #logo>
        <slot name="logo" />
      </template>
    </SiteHeader>

    <Typo
      class="showcase-index__title"
      variant="h-1"
      tag="h1"
    >
      {{ title }}
    </Typo>

    <nav
      class="showcase-index__tabs"
      data-testid="showcase-index-tabs"
    >
      <IconTab
        :icon="toursTab.icon"
        :label="toursTab.text"
        :active="isToursOpen"
        @click="activeTab = 'tours'"
      />
      <IconTab
        :icon="hotelsTab.icon"
        :label="hotelsTab.text"
        :active="isHotelsOpen"
        @click="activeTab = 'hotels'"
      />
    </nav>

    <SearchForm
      v-if="isToursOpen"
      key="tours"
      v-model="toursModel.model.value"
      :is-visible-room-button="isVisibleRoomButton"
      :form-url="formUrl"
      :search-url="searchUrl"
      :resorts-url="resortsUrl"
      size="l"
      class="showcase-index__search-form"
      @search="onToursSearch"
    />
    <SearchForm
      v-if="isHotelsOpen"
      key="hotels"
      v-model="hotelsModel.model.value"
      v-bind="HOTEL_SEARCH_FORM_TEXTS"
      :is-visible-departure="false"
      :is-visible-room-button="false"
      :form-url="hotelsFormUrl"
      :search-url="hotelsSearchUrl"
      :resorts-url="hotelsResortsUrl"
      size="l"
      class="showcase-index__search-form"
      @search="onHotelsSearch"
    />
  </div>
</template>

<style scoped lang="scss">
  .showcase-index {
    --header-background: var(--color-brand-secondary);

    @include header-background;
    @include content-rounded-edge;

    display: flex;
    flex-direction: column;
    padding: var(--spacing-l) 0 50px;

    &__title {
      color: var(--color-text-on-brand);
      line-height: 56px;
      margin: var(--spacing-xl) var(--spacing-l) 0;
      text-align: center;
    }

    &__tabs {
      display: flex;
      gap: var(--spacing-xl);
      justify-content: center;
      margin-top: var(--spacing-xxl);
    }

    &__search-form {
      background: transparent;
      margin-top: var(--spacing-xl);
      padding: 0 var(--spacing-l);
    }
  }
</style>
